import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  BondListingRouteData,
  BondRow,
  BondSortBy,
  BondSortDirection,
  BondView,
  BondsQueryParams,
} from './bonds.types';
import { BondsService } from './bonds.service';

interface BondColumn {
  key: BondSortBy;
  label: string;
  alignEnd?: boolean;
}

@Component({
  selector: 'app-bonds',
  imports: [CommonModule, RouterLink],
  templateUrl: './bonds.html',
  styleUrl: './bonds.css',
})
export class Bonds implements OnInit, OnDestroy {
  private readonly defaultPageSize = 25;
  private readonly searchDebounceMs = 300;

  private requestSubscription?: Subscription;
  private routeSubscription?: Subscription;
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  protected readonly pageSizeOptions = [10, 25, 50, 100, 200];
  protected readonly columns: BondColumn[] = [
    { key: 'dealid', label: 'Deal ID' },
    { key: 'tradeid', label: 'Trade ID' },
    { key: 'structureid', label: 'Structure ID' },
    { key: 'securityid', label: 'Security ID' },
    { key: 'cusip', label: 'CUSIP' },
    { key: 'isin', label: 'ISIN' },
    { key: 'securitytype', label: 'Security Type' },
    { key: 'tradedate', label: 'Trade Date' },
    { key: 'settlementdate', label: 'Settlement Date' },
    { key: 'maturitydate', label: 'Maturity Date' },
    { key: 'currency', label: 'Currency' },
    { key: 'notional', label: 'Notional', alignEnd: true },
    { key: 'coupon', label: 'Coupon', alignEnd: true },
    { key: 'markettype', label: 'Market Type' },
  ];

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly rows = signal<BondRow[]>([]);
  protected readonly totalRecords = signal(0);
  protected readonly totalPages = signal(1);
  protected readonly pageNumber = signal(1);
  protected readonly pageSize = signal(this.defaultPageSize);
  protected readonly searchTerm = signal('');
  protected readonly sortBy = signal<BondSortBy | null>(null);
  protected readonly sortDirection = signal<BondSortDirection | null>(null);
  protected readonly listingLabel = signal('Bonds');
  protected readonly listingLabelLowercase = computed(() => this.listingLabel().toLocaleLowerCase());
  private readonly view = signal<BondView | undefined>(undefined);

  protected readonly isFirstPage = computed(() => this.pageNumber() <= 1);
  protected readonly isLastPage = computed(() => this.pageNumber() >= this.totalPages());
  protected readonly startRecord = computed(() => {
    if (this.totalRecords() === 0) {
      return 0;
    }

    return (this.pageNumber() - 1) * this.pageSize() + 1;
  });
  protected readonly endRecord = computed(() => {
    if (this.totalRecords() === 0) {
      return 0;
    }

    return Math.min(this.pageNumber() * this.pageSize(), this.totalRecords());
  });
  protected readonly visiblePages = computed(() => {
    const current = this.pageNumber();
    const total = this.totalPages();
    const maxButtons = 5;
    const lastWindowStart = Math.max(1, total - maxButtons + 1);
    const start = Math.min(current, lastWindowStart);
    const end = Math.min(total, start + maxButtons - 1);

    const pages: number[] = [];
    for (let value = start; value <= end; value += 1) {
      pages.push(value);
    }

    return pages;
  });

  constructor(
    private readonly bondsService: BondsService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.routeSubscription = this.route.data.subscribe((data) => {
      const listing = data as BondListingRouteData;
      this.listingLabel.set(listing.label || 'Bonds');
      this.view.set(listing.view);
      this.pageNumber.set(1);
      this.loadRows({ pageNumber: 1 });
    });
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.clearSearchDebounce();
  }

  protected onRetry(): void {
    this.loadRows();
  }

  protected onSearchInput(value: string): void {
    this.searchTerm.set(value);
    this.clearSearchDebounce();

    this.searchDebounceHandle = setTimeout(() => {
      this.loadRows({ pageNumber: 1 });
    }, this.searchDebounceMs);
  }

  protected onSort(column: BondSortBy): void {
    if (this.sortBy() === column) {
      if (this.sortDirection() === 'asc') {
        this.sortDirection.set('desc');
      } else if (this.sortDirection() === 'desc') {
        this.sortBy.set(null);
        this.sortDirection.set(null);
      } else {
        this.sortDirection.set('asc');
      }
    } else {
      this.sortBy.set(column);
      this.sortDirection.set('asc');
    }

    this.loadRows({ pageNumber: 1 });
  }

  protected goToPage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.pageNumber()) {
      return;
    }

    this.loadRows({ pageNumber: page });
  }

  protected goToPreviousPage(): void {
    if (this.isFirstPage()) {
      return;
    }

    this.goToPage(this.pageNumber() - 1);
  }

  protected goToFirstPage(): void {
    this.goToPage(1);
  }

  protected goToNextPage(): void {
    if (this.isLastPage()) {
      return;
    }

    this.goToPage(this.pageNumber() + 1);
  }

  protected goToLastPage(): void {
    this.goToPage(this.totalPages());
  }

  protected onPageSizeChange(value: string): void {
    const nextPageSize = this.normalizePageSize(Number(value));
    if (nextPageSize === this.pageSize()) {
      return;
    }

    this.pageSize.set(nextPageSize);
    this.loadRows({
      pageNumber: 1,
      pageSize: nextPageSize,
    });
  }

  protected getSortIndicator(column: BondSortBy): string {
    if (this.sortBy() !== column) {
      return '';
    }

    return this.sortDirection() === 'asc' ? '\u2191' : '\u2193';
  }

  protected trackByRow(index: number, row: BondRow): string {
    return `${row.dealId}-${row.tradeId}-${index}`;
  }

  protected formatDate(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(parsed);
  }

  protected formatNumber(value: number, maximumFractionDigits: number, alwaysPositive = false): string {
    const formattedValue = alwaysPositive ? Math.abs(value) : value;

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    }).format(formattedValue);
  }

  private loadRows(overrides: BondsQueryParams = {}): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.requestSubscription?.unsubscribe();

    const query: BondsQueryParams = {
      pageNumber: overrides.pageNumber ?? this.pageNumber(),
      pageSize: overrides.pageSize ?? this.pageSize(),
      search: overrides.search ?? this.searchTerm(),
      sortBy: overrides.sortBy ?? this.sortBy() ?? undefined,
      sortDirection: overrides.sortDirection ?? this.sortDirection() ?? undefined,
      view: this.view(),
    };

    this.requestSubscription = this.bondsService.getBonds(query).subscribe({
      next: (response) => {
        this.rows.set(response.items);
        this.totalRecords.set(response.totalRecords);
        this.totalPages.set(Math.max(1, response.totalPages));
        this.pageNumber.set(response.pageNumber);
        this.pageSize.set(response.pageSize);
        this.isLoading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.totalRecords.set(0);
        this.totalPages.set(1);
        this.errorMessage.set(`Unable to load ${this.listingLabelLowercase()} data from the API.`);
        this.isLoading.set(false);
      },
    });
  }

  private normalizePageSize(value: number): number {
    if (!Number.isFinite(value) || value < 1) {
      return this.defaultPageSize;
    }

    if (value > 200) {
      return 200;
    }

    return Math.floor(value);
  }

  private clearSearchDebounce(): void {
    if (this.searchDebounceHandle !== null) {
      clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = null;
    }
  }
}